import type { ActionStep } from '../state/types';

export function renderActionHistory(actionSteps: ActionStep[] | undefined): string {
    if (!actionSteps || actionSteps.length === 0) {
        return '';
    }

    const summary = getActionSummary(actionSteps);
    
    return `
        <div class="action-history" data-expanded="false">
            <div class="action-history-header">
                <span class="action-history-summary">${summary}</span>
                <button class="action-history-toggle" aria-label="Toggle action details">
                    <svg class="action-history-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </button>
            </div>
            <div class="action-history-details">
                ${actionSteps.map((step, index) => renderActionStep(step, index)).join('')}
            </div>
        </div>
    `;
}

function getActionSummary(actionSteps: ActionStep[]): string {
    const toolCalls = actionSteps.filter(s => s.type === 'tool_call');
    const reasoning = actionSteps.filter(s => s.type === 'reasoning');
    
    const parts: string[] = [];
    
    if (toolCalls.length > 0) {
        const toolNames = [...new Set(toolCalls.map(s => s.name).filter(Boolean))];
        if (toolNames.length === 1) {
            parts.push(`1 tool: ${toolNames[0]}`);
        } else {
            parts.push(`${toolCalls.length} tools: ${toolNames.slice(0, 3).join(', ')}${toolNames.length > 3 ? '...' : ''}`);
        }
    }
    
    if (reasoning.length > 0) {
        parts.push('reasoning');
    }
    
    if (parts.length === 0) {
        return `${actionSteps.length} actions`;
    }
    
    return parts.join(', ');
}

function renderActionStep(step: ActionStep, index: number): string {
    if (step.type === 'tool_call') {
        return renderToolCallStep(step, index);
    } else {
        return renderReasoningStep(step, index);
    }
}

function renderToolCallStep(step: ActionStep, index: number): string {
    let argsDisplay = '';
    if (step.arguments) {
        try {
            const parsed = JSON.parse(step.arguments);
            argsDisplay = `<pre class="action-step-args">${escapeHtml(JSON.stringify(parsed, null, 2))}</pre>`;
        } catch {
            argsDisplay = `<code class="action-step-args">${escapeHtml(step.arguments)}</code>`;
        }
    }
    
    return `
        <div class="action-step action-step-tool-call" data-index="${index}">
            <div class="action-step-header">
                <span class="action-step-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                    </svg>
                </span>
                <span class="action-step-name">${escapeHtml(step.name || 'unknown')}</span>
            </div>
            ${argsDisplay}
        </div>
    `;
}

function renderReasoningStep(step: ActionStep, index: number): string {
    const content = step.content || '';
    
    return `
        <div class="action-step action-step-reasoning" data-index="${index}">
            <div class="action-step-header">
                <span class="action-step-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 16v-4"></path>
                        <path d="M12 8h.01"></path>
                    </svg>
                </span>
                <span class="action-step-name">Reasoning</span>
            </div>
            <div class="action-step-content">${escapeHtml(content)}</div>
        </div>
    `;
}

function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

export function setupActionHistoryToggle(container: HTMLElement): void {
    container.addEventListener('click', (e) => {
        const header = (e.target as HTMLElement).closest('.action-history-header');
        if (header) {
            const history = header.closest('.action-history');
            if (history) {
                const isExpanded = history.getAttribute('data-expanded') === 'true';
                history.setAttribute('data-expanded', isExpanded ? 'false' : 'true');
            }
        }
    });
}